<!-- Generic tag editing modal - to be used in a custom component for parent api calls -->
<template>
  <BaseModal
    card-width="490px"
    no-padding
    no-toolbar-padding
    no-exit
    @close="$emit('close')"
  >
    <template #toolbar>
      <div class="flex w-full gap-3 pb-5 pt-1">
        <div
          id="tags-brand-container"
          class="relative"
        >
          <LensTagsIcon class="text-icon-normal w-8 h-8" />
        </div>
        <div class="flex-grow flex flex-col">
          <div class="font-medium text-text-loud">
            {{ header }}
          </div>
          <div class="text-sm text-text-normal">
            Rename, change color, add or delete tags
          </div>
        </div>
        <div class="ml-auto">
          <div
            id="close-wrapper"
            @click="$emit('close')"
          >
            <TagRemoveIcon
              stroke="#5E6678"
              class="cursor-pointer"
            />
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <div class="relative w-full border-t border-neutral-50 pr-1 rounded-b-xl overflow-hidden">
        <div
          ref="tagsList"
          class="tags-list w-full flex flex-col pl-1.5 pr-0.5 pb-1.5"
        >
          <!-- Add new tag input row -->
          <div
            v-on-clickaway="() => {inputFocused = false; showNewTagColorPicker = false}"
            class="relative w-full flex items-center gap-2 py-1 mt-3 mb-1 rounded-lg bg-transparent hover:bg-background-normal
          transition-colors ease-in-out"
            :class="{'bg-background-normal': inputFocused}"
          >
            <div class="ml-3">
              <div
                v-if="newTagName !== ''"
                class="hover-shadow p-1 rounded-md bg-transparent hover:bg-white transition ease-in-out cursor-pointer"
                @click="showNewTagColorPicker = !showNewTagColorPicker"
              >
                <TagIcon
                  :stroke="colors[newTagColor].stroke"
                  :fill="colors[newTagColor].fill"
                />
              </div>
              <div
                v-else
                class="p-1"
              >
                <PlusIcon stroke="#808899" />
              </div>
            </div>
            <input
              id="add-tag-input"
              ref="addTagInput"
              v-model="newTagName"
              class="flex-grow py-1 px-2 rounded-md text-sm outline-none bg-transparent"
              placeholder="Add new tag"
              autocomplete="off"
              spellcheck="false"
              @keyup.enter.prevent="createTag"
              @focus="inputFocused = true"
              @blur="() => {if (newTagName === '') {inputFocused = false; newTagColor = 'gray'}}"
            >
            <div
              v-if="isCreatingTag"
              class="p-1 ml-auto"
              :style="{marginRight: '13px'}"
            >
              <BaseLoadingSpinnerCircle class="text-icon-normal" />
            </div>
            <div
              v-else-if="newTagName !== ''"
              class="hover-shadow group p-1 ml-auto mr-3 rounded-md bg-transparent hover:bg-white transition ease-in-out cursor-pointer"
              @click="createTag"
            >
              <CheckmarkIcon class-name="text-icon-normal group-hover:text-black transition ease-in-out" />
            </div>
            <TagColorPicker
              v-if="showNewTagColorPicker"
              v-on-clickaway="() => {showNewTagColorPicker = false}"
              location="bottom"
              @colorChange="newTagColorChange"
            />
          </div>
          <!-- Render the user's tags -->
          <TagRow
            v-for="tag in tags "
            :key="tag.id"
            :tag="tag"
            :count-text="countText"
            @edit="editTag"
            @delete="deleteTag"
          />
        </div>
        <!-- Fade overlay -->
        <div id="list-fade-overlay-bot" />
      </div>
    </template>
  </BaseModal>
</template>

<script>
import TagRow from './DetailsDrawerComponents/TagRow.vue'
import TagColorPicker from './DetailsDrawerComponents/TagColorPicker.vue'
import PlusIcon from '../globals/Icons/PlusIcon.vue'
import TagIcon from '../globals/Icons/TagIcon.vue'
import CheckmarkIcon from '../globals/Icons/CheckmarkIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import LensTagsIcon from '../globals/Icons/LensIcons/LensTagsIcon.vue'
import TAG_COLORS from '../../utils/TagColors'
import { mixin as clickaway } from 'vue-clickaway2'

export default {
  name: 'EditTagsModalV2',
  components: {
    TagRow,
    TagColorPicker,
    PlusIcon,
    TagIcon,
    CheckmarkIcon,
    TagRemoveIcon,
    LensTagsIcon
  },
  mixins: [clickaway],
  props: {
    header: {
      type: String,
      default: 'Edit Tags'
    },
    tags: {
      type: Array,
      default: () => []
    },
    countText: {
      type: String,
      default: 'tag'
    }
  },
  data () {
    return {
      newTagName: '',
      newTagColor: 'gray',
      isCreatingTag: false,
      inputFocused: false,
      showNewTagColorPicker: false,
      colors: TAG_COLORS
    }
  },
  methods: {
    reset () {
      this.newTagName = ''
      this.newTagColor = 'gray'
      this.isCreatingTag = false
      this.inputFocused = false
      this.showNewTagColorPicker = false
    },
    async deleteTag (tag) {
      this.$emit('deleteTag', tag.id)
    },
    async editTag (editedTag) {
      this.$emit('editTag', editedTag)
    },
    async createTag () {
      this.$emit('createTag', {
        value: this.newTagName,
        color: this.newTagColor,
        lensId: this.$route.params.lensId
      })
    },
    newTagColorChange (color) {
      this.newTagColor = color
    }
  }
}
</script>

<style scoped lang="css">
  @property --scrollbar-thumb-bg {
    syntax: '<color>';
    inherits: true;
    initial-value: white;
  }

  .tags-list {
    max-height: 394px;
    overflow-y: scroll;
    transition: --scrollbar-thumb-bg 250ms ease-in-out;
  }
  .tags-list::-webkit-scrollbar {
    width: 3px;
  }
  .tags-list::-webkit-scrollbar-thumb {
    /* We use an animated CSS variable to apply the scrollbar fading transitions */
    /* This is because scrollbar pseudo-elements do not support the 'transition' property */
    background-color: var(--scrollbar-thumb-bg);
    border-radius: 18px;
  }
  #add-tag-input {
    transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
    background-color: transparent;
    max-width: 325px;
  }
  #list-fade-overlay-bot {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 18px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%);
    z-index: 20;
    pointer-events: none;
  }
  #add-tag-input:focus {
    box-shadow: 0px 1px 2px 0px rgba(162, 172, 186, 0.08), 0px 1px 2px 0px rgba(162, 172, 186, 0.16);
    background-color: white !important;
  }
  #add-tag-input::placeholder {
    transition: color 150ms ease-in-out;
    color: #303546;
    font-weight: 500;
  }
  #add-tag-input:focus::placeholder {
    color: #808899;
    font-weight: 400;
  }
  #close-wrapper {
    transition: transform 150ms ease-in-out;
    transform: scale(1);
  }
  #close-wrapper:hover {
    transform: scale(1.10);
  }
  .hover-shadow {
    transition: box-shadow 150ms ease-in-out;
  }
  .hover-shadow:hover {
    box-shadow: 0px 24px 32px -12px rgba(53, 57, 75, 0.12), 0px 1px 2px 0px rgba(162, 172, 186, 0.08), 0px 1px 2px 0px rgba(162, 172, 186, 0.16);
  }
</style>
